import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { updateVarietyPresentations, uploadFile } from '../api';
import useProductFormatOptions from '../hooks/useProductFormatOptions';
import { getProductFormatLabel, resolvePriceProductFormatCode } from '../utils/clientType';
import { buildSettingsPath } from '../config/settingsNavigation';
import VarietyEditorDialog from './varietyPricing/VarietyEditorDialog';
import { hasChangedFormatIdentity, haveConflictingFormatIdentity, isRepeatableProductFormat, packWeightLabel } from '../utils/varietyFormatIdentity';
import { applySharedRetailConfiguration } from '../utils/sharedRetailConfiguration';

const FORMAT_FIELDS = [
  'productFormatCode',
  'description',
  'barcode',
  'metricUnit',
  'unitWeightKg',
  'baseQuantityPerUnit',
  'useScaleWeight',
  'priceImage',
];
const IDENTITY_FIELDS = ['varietyName', 'description', 'varietyImage'];
const DISCARD_MESSAGE = '¿Descartar los cambios sin guardar?';

const trimmed = (value) => String(value ?? '').trim();
const optionalText = (value) => trimmed(value) || null;
const optionalNumber = (value) => {
  const normalized = trimmed(value);
  if (!normalized) return null;
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : `invalid:${normalized}`;
};

const buildIdentityValues = (variety = {}) => ({
  varietyName: String(variety.varietyName || ''),
  description: String(variety.description || ''),
  varietyImage: String(variety.varietyImage || ''),
});

const canonicalIdentity = (identity = {}, imageUrl = identity.varietyImage) => ({
  varietyName: trimmed(identity.varietyName),
  description: optionalText(identity.description),
  varietyImage: optionalText(imageUrl),
});

const buildFormatValues = (format = {}) => ({
  productFormatCode: resolvePriceProductFormatCode(format),
  productFormatName: format.productFormatName || format.ProductFormat?.name || format.productFormat?.name || '',
  description: String(format.description || ''),
  barcode: String(format.barcode || ''),
  metricUnit: String(format.metricUnit || 'unidad'),
  unitWeightKg: format.unitWeightKg ?? '',
  baseQuantityPerUnit: format.baseQuantityPerUnit ?? '',
  useScaleWeight: Boolean(format.useScaleWeight),
  priceImage: String(format.priceImage || ''),
});

const canonicalFormat = (format = {}, imageUrl = format.priceImage) => ({
  productFormatCode: trimmed(format.productFormatCode),
  description: optionalText(format.description),
  barcode: optionalText(format.barcode),
  metricUnit: trimmed(format.metricUnit),
  unitWeightKg: optionalNumber(format.unitWeightKg),
  baseQuantityPerUnit: optionalNumber(format.baseQuantityPerUnit),
  useScaleWeight: Boolean(format.useScaleWeight),
  priceImage: optionalText(imageUrl),
});

const valuesMatch = (left, right) => JSON.stringify(left) === JSON.stringify(right);
const changedFields = (current, baseline, fields) => fields.reduce((patch, field) => {
  if (!Object.is(current[field], baseline[field])) patch[field] = current[field];
  return patch;
}, {});

const buildEditSession = (variety = {}) => {
  const identity = buildIdentityValues(variety);
  return {
    identity: {
      ...identity,
      imageFile: null,
      previewUrl: '',
      baseline: canonicalIdentity(identity),
    },
    formats: (variety.VarietyPrices || []).map((format) => {
      const values = buildFormatValues(format);
      return {
        key: `format-${format.id}`,
        id: format.id,
        ...values,
        imageFile: null,
        previewUrl: '',
        pendingDelete: false,
        baseline: canonicalFormat(values),
      };
    }),
  };
};

const sessionIsDirty = (session) => {
  if (!session) return false;
  if (
    session.identity.imageFile
    || !valuesMatch(canonicalIdentity(session.identity), session.identity.baseline)
  ) return true;
  return session.formats.some((format) => (
    !format.id
    || format.pendingDelete
    || format.imageFile
    || !valuesMatch(canonicalFormat(format), format.baseline)
  ));
};

const safeErrorId = (fieldKey) => `presentation-error-${fieldKey.replace(/[^a-zA-Z0-9_-]/g, '-')}`;

const duplicateFormatCodes = (formats, formatOptions) => {
  const active = formats.filter((format) => !format.pendingDelete);
  return [...new Set(active.filter((format) => active.some((other) => other !== format
    && haveConflictingFormatIdentity(format, other, formatOptions))).map(resolvePriceProductFormatCode))];
};

const sortPresentationsByFormat = (presentations, formatOptions) => {
  const formatPositions = new Map(formatOptions.map((option, index) => [option.value, index + 1]));
  const position = (presentation) => {
    const configured = presentation.productFormatSortOrder
      ?? presentation.ProductFormat?.sortOrder
      ?? presentation.productFormat?.sortOrder;
    if (configured !== undefined && configured !== null && Number.isFinite(Number(configured))) {
      return Number(configured);
    }
    return formatPositions.get(resolvePriceProductFormatCode(presentation)) ?? Number.MAX_SAFE_INTEGER;
  };
  const name = (presentation) => presentation.productFormatName
    || presentation.ProductFormat?.name
    || presentation.productFormat?.name
    || getProductFormatLabel(resolvePriceProductFormatCode(presentation), formatOptions);
  return presentations.slice().sort((left, right) => (
    position(left) - position(right)
    || String(name(left)).localeCompare(String(name(right)))
    || Number(left.productFormatId ?? left.ProductFormat?.id ?? left.productFormat?.id ?? 0)
      - Number(right.productFormatId ?? right.ProductFormat?.id ?? right.productFormat?.id ?? 0)
  ));
};

const PresentationItem = ({
  branchId,
  variety,
  fallbackImage,
  targetFormatId,
  canUpdateProduct,
  canManageFormats = canUpdateProduct,
  canReadPrices = false,
  onDeleteVariety,
  onVarietySaved,
}) => {
  const navigate = useNavigate();
  const formatOptions = useProductFormatOptions();
  const formats = useMemo(
    () => sortPresentationsByFormat(variety.VarietyPrices || [], formatOptions),
    [variety.VarietyPrices, formatOptions]
  );
  const targetBelongsToVariety = formats.some(
    (format) => String(format.id) === String(targetFormatId)
  );
  const [showDetails, setShowDetails] = useState(targetBelongsToVariety);
  const [editSession, setEditSession] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [pendingFocusKey, setPendingFocusKey] = useState('');
  const nextFormatKeyRef = useRef(0);
  const savingRef = useRef(false);
  const editButtonRef = useRef(null);
  const deleteButtonRef = useRef(null);
  const restoreDeleteFocusRef = useRef(false);
  const restoreEditFocusRef = useRef(false);
  const fieldRefs = useRef(new Map());
  const previewUrlsRef = useRef(new Set());
  const uploadedImagesRef = useRef(new Map());
  const editing = Boolean(editSession);
  const dirty = sessionIsDirty(editSession);
  const repeatedCodes = duplicateFormatCodes(editing ? editSession.formats : formats, formatOptions);
  const repeatedLabels = repeatedCodes.map((code) => getProductFormatLabel(code, formatOptions)).join(', ');
  const unusedFormatOptions = formatOptions.filter((option) => isRepeatableProductFormat(option) || !editSession?.formats.some(
    (format) => !format.pendingDelete && format.productFormatCode === option.value
  ));

  const revokePreview = (url) => {
    if (!url || !previewUrlsRef.current.has(url)) return;
    URL.revokeObjectURL?.(url);
    previewUrlsRef.current.delete(url);
  };
  const clearPreviews = () => {
    previewUrlsRef.current.forEach((url) => URL.revokeObjectURL?.(url));
    previewUrlsRef.current.clear();
  };
  const createPreview = (file) => {
    if (!file || typeof URL === 'undefined' || typeof URL.createObjectURL !== 'function') return '';
    const url = URL.createObjectURL(file);
    previewUrlsRef.current.add(url);
    return url;
  };

  useEffect(() => () => clearPreviews(), []);
  useEffect(() => {
    if (targetBelongsToVariety) setShowDetails(true);
  }, [targetBelongsToVariety, targetFormatId]);
  useEffect(() => {
    if (!pendingFocusKey) return;
    const focusControl = () => {
      const control = fieldRefs.current.get(pendingFocusKey);
      control?.focus();
      control?.scrollIntoView?.({ block: 'nearest' });
    };
    focusControl();
    const focusTimer = window.setTimeout(() => {
      focusControl();
      setPendingFocusKey('');
    }, 0);
    return () => window.clearTimeout(focusTimer);
  }, [editSession, pendingFocusKey]);
  useEffect(() => {
    if (editing || !showDetails || !restoreEditFocusRef.current) return;
    restoreEditFocusRef.current = false;
    editButtonRef.current?.focus();
  }, [editing, showDetails]);

  useEffect(() => {
    if (deleting || !restoreDeleteFocusRef.current) return;
    restoreDeleteFocusRef.current = false;
    deleteButtonRef.current?.focus();
  }, [deleting]);

  const deleteCurrentVariety = async () => {
    if (!canUpdateProduct || !onDeleteVariety || savingRef.current) return;
    savingRef.current = true;
    setDeleting(true);
    setError('');
    try {
      await onDeleteVariety(variety.id);
    } catch (deleteError) {
      setError(deleteError?.response?.data?.message || 'No se pudo eliminar la variedad. Inténtalo nuevamente.');
    } finally {
      savingRef.current = false;
      restoreDeleteFocusRef.current = true;
      setDeleting(false);
    }
  };

  const registerField = (fieldKey) => (element) => {
    if (element) fieldRefs.current.set(fieldKey, element);
    else fieldRefs.current.delete(fieldKey);
  };
  const clearEditorState = () => {
    clearPreviews();
    uploadedImagesRef.current.clear();
    fieldRefs.current.clear();
    setEditSession(null);
    setFieldErrors({});
    setPendingFocusKey('');
    setError('');
  };
  const closeDetails = () => {
    if (savingRef.current || (dirty && !window.confirm(DISCARD_MESSAGE))) return;
    restoreEditFocusRef.current = false;
    clearEditorState();
    setShowDetails(false);
  };
  const cancelEdit = () => {
    if (savingRef.current || (dirty && !window.confirm(DISCARD_MESSAGE))) return;
    restoreEditFocusRef.current = true;
    clearEditorState();
  };
  const startEditing = () => {
    restoreEditFocusRef.current = false;
    clearPreviews();
    uploadedImagesRef.current.clear();
    nextFormatKeyRef.current = 0;
    setFieldErrors({});
    setError('');
    setEditSession(buildEditSession({ ...variety, VarietyPrices: formats }));
    setPendingFocusKey('identity.varietyName');
  };
  const clearFieldError = (fieldKey) => {
    setFieldErrors((current) => {
      if (!current[fieldKey]) return current;
      const next = { ...current };
      delete next[fieldKey];
      return next;
    });
    setError('');
  };
  const updateIdentityField = (field, value) => {
    clearFieldError(`identity.${field}`);
    setEditSession((current) => ({
      ...current,
      identity: { ...current.identity, [field]: value },
    }));
  };
  const updateFormatField = (formatKey, field, value) => {
    const fieldKey = `${formatKey}.${field}`;
    if (field === 'barcode' || field === 'productFormatCode') {
      setFieldErrors((current) => Object.fromEntries(
        Object.entries(current).filter(([key]) => (
          key !== fieldKey && !key.endsWith('.barcode')
        ))
      ));
      setError('');
    } else {
      clearFieldError(fieldKey);
    }
    setEditSession((current) => ({
      ...current,
      formats: current.formats.map((format) => (
        format.key === formatKey ? { ...format, [field]: value,
          ...(field === 'productFormatCode' ? { productFormatName: formatOptions.find((option) => option.value === value)?.label || '' } : {}),
        } : format
      )),
    }));
  };
  const replaceIdentityImage = (file) => {
    revokePreview(editSession?.identity.previewUrl);
    uploadedImagesRef.current.delete('identity');
    setEditSession((current) => ({
      ...current,
      identity: {
        ...current.identity,
        imageFile: file,
        previewUrl: createPreview(file),
      },
    }));
    setError('');
  };
  const removeIdentityImage = () => {
    revokePreview(editSession?.identity.previewUrl);
    uploadedImagesRef.current.delete('identity');
    setEditSession((current) => ({
      ...current,
      identity: {
        ...current.identity,
        varietyImage: '',
        imageFile: null,
        previewUrl: '',
      },
    }));
    setError('');
  };
  const replaceFormatImage = (formatKey, file) => {
    const currentFormat = editSession?.formats.find((format) => format.key === formatKey);
    revokePreview(currentFormat?.previewUrl);
    uploadedImagesRef.current.delete(formatKey);
    setEditSession((current) => ({
      ...current,
      formats: current.formats.map((format) => (
        format.key === formatKey
          ? { ...format, imageFile: file, previewUrl: createPreview(file) }
          : format
      )),
    }));
    setError('');
  };
  const removeFormatImage = (formatKey) => {
    const currentFormat = editSession?.formats.find((format) => format.key === formatKey);
    revokePreview(currentFormat?.previewUrl);
    uploadedImagesRef.current.delete(formatKey);
    setEditSession((current) => ({
      ...current,
      formats: current.formats.map((format) => (
        format.key === formatKey
          ? { ...format, priceImage: '', imageFile: null, previewUrl: '' }
          : format
      )),
    }));
    setError('');
  };
  const addFormat = () => {
    if (!unusedFormatOptions.length) return;
    nextFormatKeyRef.current += 1;
    const key = `new-${variety.id}-${nextFormatKeyRef.current}`;
    setEditSession((current) => ({
      ...current,
      formats: [...current.formats, {
        key,
        id: null,
        productFormatCode: unusedFormatOptions[0].value,
        productFormatName: unusedFormatOptions[0].label,
        description: '',
        barcode: '',
        metricUnit: 'unidad',
        unitWeightKg: '',
        useScaleWeight: false,
        priceImage: '',
        imageFile: null,
        previewUrl: '',
        pendingDelete: false,
        baseline: null,
      }],
    }));
    setFieldErrors({});
    setError('');
    setPendingFocusKey(`${key}.productFormatCode`);
  };
  const stageDeleteFormat = (formatKey) => {
    const currentFormat = editSession?.formats.find((format) => format.key === formatKey);
    if (!currentFormat) return;
    if (!currentFormat.id) {
      revokePreview(currentFormat.previewUrl);
      uploadedImagesRef.current.delete(formatKey);
    }
    setFieldErrors((current) => Object.fromEntries(
      Object.entries(current).filter(([key]) => !key.startsWith(`${formatKey}.`))
    ));
    setError('');
    setEditSession((current) => ({
      ...current,
      formats: currentFormat.id
        ? current.formats.map((format) => (
          format.key === formatKey
            ? { ...format, pendingDelete: true }
            : format
        ))
        : current.formats.filter((format) => format.key !== formatKey),
    }));
  };
  const undoDeleteFormat = (formatKey) => {
    setEditSession((current) => ({
      ...current,
      formats: current.formats.map((format) => (
        format.key === formatKey ? { ...format, pendingDelete: false } : format
      )),
    }));
    setError('');
  };

  const validateSession = () => {
    const nextErrors = {};
    if (!trimmed(editSession?.identity.varietyName)) {
      nextErrors['identity.varietyName'] = 'El nombre de la variedad es obligatorio.';
    }
    const allowedCodes = new Set(formatOptions.map((option) => String(option.value)));
    const seenBarcodes = new Map();
    editSession?.formats.forEach((format) => {
      if (format.pendingDelete) return;
      const code = trimmed(format.productFormatCode);
      const codeChanged = !format.id || code !== format.baseline?.productFormatCode;
      if (!code || (codeChanged && !allowedCodes.has(code))) {
        nextErrors[`${format.key}.productFormatCode`] = 'Selecciona un formato activo.';
      } else if (hasChangedFormatIdentity(format, format.baseline, formatOptions) && editSession.formats.some((other) => other !== format && !other.pendingDelete && haveConflictingFormatIdentity(format, other, formatOptions))) {
        nextErrors[`${format.key}.productFormatCode`] = isRepeatableProductFormat(format, formatOptions)
          ? 'Esta variedad ya tiene un pack con la misma unidad y peso.'
          : 'Esta variedad ya tiene ese tipo de formato.';
      }
      const barcode = trimmed(format.barcode);
      if (barcode && !/^\d{1,7}$/.test(barcode)) {
        nextErrors[`${format.key}.barcode`] = 'Usa entre 1 y 7 dígitos.';
      } else if (barcode) {
        const barcodeKey = String(Number(barcode));
        const previous = seenBarcodes.get(barcodeKey);
        const changed = !format.id || barcode !== trimmed(format.baseline?.barcode);
        const previousChanged = previous && (!previous.id || trimmed(previous.barcode) !== trimmed(previous.baseline?.barcode));
        if (previous && (changed || previousChanged)) {
          nextErrors[`${changed ? format.key : previous.key}.barcode`] = 'Este código ya se usa en otro formato del borrador.';
        }
        seenBarcodes.set(barcodeKey, format);
      }
      const weight = trimmed(format.unitWeightKg);
      if (weight && (!Number.isFinite(Number(weight)) || Number(weight) <= 0)) {
        nextErrors[`${format.key}.unitWeightKg`] = 'Debe ser un número mayor a 0.';
      }
    });
    setFieldErrors(nextErrors);
    const firstInvalidKey = Object.keys(nextErrors)[0];
    if (firstInvalidKey) {
      const control = fieldRefs.current.get(firstInvalidKey);
      control?.focus();
      control?.scrollIntoView?.({ block: 'nearest' });
    }
    return !firstInvalidKey;
  };
  const uploadSelectedImage = async (cacheKey, file, failureMessage) => {
    if (!file) return null;
    const cached = uploadedImagesRef.current.get(cacheKey);
    if (cached?.file === file && cached.url) return cached.url;
    const uploaded = await uploadFile(file);
    if (!uploaded?.url) throw new Error(failureMessage);
    uploadedImagesRef.current.set(cacheKey, { file, url: uploaded.url });
    return uploaded.url;
  };
  const saveChanges = async () => {
    if (!editSession || savingRef.current || !dirty || !validateSession()) return;
    savingRef.current = true;
    setSaving(true);
    setError('');
    try {
      const uploadedUrls = new Map();
      const uploadJobs = [];
      if (editSession.identity.imageFile) {
        uploadJobs.push(uploadSelectedImage(
          'identity',
          editSession.identity.imageFile,
          'No se pudo subir la imagen de la variedad.'
        ).then((url) => uploadedUrls.set('identity', url)));
      }
      editSession.formats.forEach((format) => {
        if (format.pendingDelete || !format.imageFile) return;
        uploadJobs.push(uploadSelectedImage(
          format.key,
          format.imageFile,
          'No se pudo subir la imagen de la presentación.'
        ).then((url) => uploadedUrls.set(format.key, url)));
      });
      const uploadResults = await Promise.allSettled(uploadJobs);
      const failedUpload = uploadResults.find((result) => result.status === 'rejected');
      if (failedUpload) {
        throw failedUpload.reason instanceof Error
          ? failedUpload.reason
          : new Error(String(failedUpload.reason || 'No se pudo subir una imagen.'));
      }

      const identityValues = canonicalIdentity(
        editSession.identity,
        uploadedUrls.get('identity') || editSession.identity.varietyImage
      );
      const create = [];
      const update = [];
      const deleteIds = [];
      editSession.formats.forEach((format) => {
        if (format.pendingDelete) {
          if (format.id) deleteIds.push(format.id);
          return;
        }
        const values = canonicalFormat(
          format,
          uploadedUrls.get(format.key) || format.priceImage
        );
        if (!format.id) create.push(values);
        else {
          const patch = changedFields(values, format.baseline, FORMAT_FIELDS);
          if (Object.keys(patch).length) update.push({ id: format.id, ...patch });
        }
      });
      const savedVariety = await updateVarietyPresentations(branchId, variety.id, {
        variety: changedFields(
          identityValues,
          editSession.identity.baseline,
          IDENTITY_FIELDS
        ),
        formats: { create, update, deleteIds },
      });
      savingRef.current = false;
      setSaving(false);
      restoreEditFocusRef.current = true;
      clearEditorState();
      try {
        await onVarietySaved?.(savedVariety);
      } catch {
        // The server batch already committed; the catalog invalidation event can reconcile local state.
      }
      return;
    } catch (requestError) {
      setError(
        requestError?.response?.data?.message
        || requestError?.message
        || 'No se pudieron guardar los cambios.'
      );
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  const compactImage = variety.varietyImage
    || formats[0]?.priceImage
    || fallbackImage
    || 'https://placehold.co/200';
  const identityDisplayImage = editSession?.identity.previewUrl
    || editSession?.identity.varietyImage
    || fallbackImage
    || 'https://placehold.co/200';
  const fieldProps = (fieldKey) => ({
    ref: registerField(fieldKey),
    'aria-invalid': fieldErrors[fieldKey] ? 'true' : undefined,
    'aria-describedby': fieldErrors[fieldKey] ? safeErrorId(fieldKey) : undefined,
  });
  const renderFieldError = (fieldKey) => fieldErrors[fieldKey] && (
    <p id={safeErrorId(fieldKey)} role="alert" className="mt-1 text-[11px] font-medium text-red-700">
      {fieldErrors[fieldKey]}
    </p>
  );

  return (
    <>
      <article className="min-h-[130px] overflow-hidden rounded-lg border border-gray-200 bg-white shadow transition-shadow hover:shadow-md">
        <button
          type="button"
          onClick={() => setShowDetails(true)}
          className="flex min-h-[130px] w-full cursor-pointer flex-col justify-between p-3 text-left"
          aria-haspopup="dialog"
          aria-label={`Ver detalles de ${variety.varietyName}`}
        >
          <div className="mb-2 flex items-center gap-3">
            <img
              src={compactImage}
              alt=""
              className="h-12 w-12 shrink-0 rounded-lg border border-gray-100 object-cover shadow-sm"
              onError={(event) => {
                event.currentTarget.onerror = null;
                event.currentTarget.src = 'https://placehold.co/200';
              }}
            />
            <div className="min-w-0 flex-1">
              <h3 className="break-words text-sm font-semibold text-gray-950">{variety.varietyName}</h3>
              <span className="text-xs text-gray-500">
                {formats.length} {formats.length === 1 ? 'formato' : 'formatos'}
              </span>
            </div>
          </div>
          {duplicateFormatCodes(formats, formatOptions).length > 0 && (
            <span className="mb-2 rounded-md bg-amber-50 px-2 py-1 text-xs font-semibold text-amber-800">Formatos repetidos · Revisar</span>
          )}
          <p className="text-center text-sm font-bold text-gray-900">[Ver detalles]</p>
        </button>
      </article>

      <VarietyEditorDialog
        isOpen={showDetails}
        eyebrow={editing ? 'Editar variedad y formatos' : 'Detalle de variedad'}
        title={variety.varietyName}
        description={editing
          ? 'Los cambios de la variedad y sus formatos se aplicarán juntos al guardar.'
          : (variety.description || `${formats.length} ${formats.length === 1 ? 'formato' : 'formatos'}`)}
        onClose={closeDetails}
        busy={saving || deleting}
        closeLabel={`Cerrar detalle de ${variety.varietyName}`}
        headerActions={canUpdateProduct && !editing ? (
          <>
            <button ref={editButtonRef} type="button" onClick={startEditing} disabled={deleting} className="min-h-10 rounded-lg border border-gray-300 bg-white px-3 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50 sm:text-sm">
              Editar
            </button>
            <button ref={deleteButtonRef} type="button" onClick={deleteCurrentVariety} disabled={deleting} className="min-h-10 rounded-lg bg-red-600 px-3 py-2 text-xs font-semibold text-white hover:bg-red-700 disabled:opacity-50 sm:text-sm">
              {deleting ? 'Eliminando...' : 'Eliminar'}
            </button>
          </>
        ) : null}
        footerNote="Los costos, reglas y precios se configuran en la lista de precios."
        footerActions={editing ? (
          <>
            <button type="button" onClick={cancelEdit} disabled={saving} className="min-h-10 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50">
              Cancelar edición
            </button>
            <button type="button" onClick={saveChanges} disabled={saving || !dirty} className="min-h-10 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50">
              {saving ? 'Guardando...' : 'Guardar cambios'}
            </button>
          </>
        ) : (
          <button type="button" onClick={closeDetails} disabled={deleting} className="min-h-10 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50">
            Cerrar
          </button>
        )}
      >
        <div className="space-y-4">
          {error && <p role="alert" className="rounded-md bg-red-50 px-3 py-2 text-xs font-medium text-red-700">{error}</p>}
          {repeatedCodes.length > 0 && (
            <div role="alert" className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-950">
              <p className="font-semibold">Formatos repetidos: {repeatedLabels}</p>
              <p className="mt-1">Solo Pack permite varias presentaciones, con distinta unidad o peso. Cambia el formato o elimina las presentaciones repetidas para resolverlo.</p>
              {!editing && canUpdateProduct && canManageFormats && <button type="button" onClick={startEditing} className="mt-2 min-h-8 font-semibold underline">Resolver formatos repetidos</button>}
            </div>
          )}
          {editing && (
            <section className="rounded-xl border border-blue-200 bg-blue-50/60 p-3" aria-labelledby={`variety-identity-${variety.id}`}>
              <h3 id={`variety-identity-${variety.id}`} className="text-sm font-semibold text-gray-950">Datos de la variedad</h3>
              <fieldset disabled={saving} className="mt-3 grid gap-3 sm:grid-cols-[120px_minmax(0,1fr)_minmax(0,1fr)]">
                <div className="sm:row-span-2">
                  <img src={identityDisplayImage} alt="Vista previa de la variedad" className="h-24 w-24 rounded-lg border border-gray-200 bg-white object-cover" />
                </div>
                <label className="text-xs font-medium text-gray-700">
                  Nombre de la variedad
                  <input {...fieldProps('identity.varietyName')} value={editSession.identity.varietyName} onChange={(event) => updateIdentityField('varietyName', event.target.value)} className="mt-1 w-full rounded-md border border-gray-300 bg-white px-2.5 py-2 text-sm" />
                  {renderFieldError('identity.varietyName')}
                </label>
                <label className="text-xs font-medium text-gray-700">
                  Descripción de la variedad
                  <input value={editSession.identity.description} onChange={(event) => updateIdentityField('description', event.target.value)} className="mt-1 w-full rounded-md border border-gray-300 bg-white px-2.5 py-2 text-sm" />
                </label>
                <div className="text-xs font-medium text-gray-700 sm:col-span-2">
                  Imagen de la variedad
                  <input aria-label="Imagen de la variedad" type="file" accept="image/*" onChange={(event) => replaceIdentityImage(event.target.files?.[0] || null)} className="mt-1 block w-full text-xs" />
                  {(editSession.identity.imageFile || editSession.identity.varietyImage) && (
                    <button type="button" onClick={removeIdentityImage} className="mt-2 rounded-md border border-red-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-50">
                      Quitar imagen de la variedad
                    </button>
                  )}
                </div>
              </fieldset>
            </section>
          )}

          <div className="space-y-3">
            {applySharedRetailConfiguration(editing ? editSession.formats : formats).map((entry) => {
              const format = editing ? entry : buildFormatValues(entry);
              const formatId = entry.id;
              const formatKey = editing ? entry.key : String(entry.id);
              const formatLabel = getProductFormatLabel(format.productFormatCode, formatOptions);
              const detailsId = `format-details-${variety.id}-${formatId || formatKey}`;
              const pendingDelete = Boolean(editing && entry.pendingDelete);
              const isNew = Boolean(editing && !entry.id);
              const isHighlighted = formatId && String(targetFormatId) === String(formatId);
              const formatImage = editing
                ? (entry.previewUrl || entry.priceImage || identityDisplayImage)
                : (entry.priceImage || variety.varietyImage || fallbackImage || 'https://placehold.co/80');
              const currentCodeIsInactive = editing
                && entry.id
                && !formatOptions.some((option) => String(option.value) === String(entry.productFormatCode));
              return (
                <article
                  key={formatKey}
                  id={formatId ? `product-presentation-${formatId}` : `product-presentation-${formatKey}`}
                  className={`overflow-hidden rounded-lg border bg-white transition-opacity ${pendingDelete ? 'border-red-300 bg-red-50/40 opacity-70' : (isHighlighted ? 'border-blue-400 ring-2 ring-blue-100' : 'border-gray-200')}`}
                >
                  <div className="flex flex-col gap-2 px-2.5 py-2 sm:flex-row sm:items-center sm:justify-between sm:px-3">
                    <div className="flex min-w-0 items-center gap-2.5">
                      <div className="h-10 w-10 flex-none overflow-hidden rounded-md border border-gray-200 bg-gray-50">
                        <img
                          src={formatImage}
                          alt={`Imagen del formato ${formatLabel}`}
                          className="h-full w-full object-cover"
                          onError={(event) => {
                            event.currentTarget.onerror = null;
                            event.currentTarget.src = 'https://placehold.co/80';
                          }}
                        />
                      </div>
                      <div className="min-w-0">
                        <h4 className="text-sm font-semibold text-gray-950">{isNew ? `Nuevo formato · ${formatLabel}` : formatLabel}</h4>
                        {!pendingDelete && repeatedCodes.includes(format.productFormatCode) && <p className="text-xs font-semibold text-amber-800">Formato repetido</p>}
                        <p className="truncate text-xs text-gray-500">{format.metricUnit || 'Sin unidad'}{packWeightLabel(format, formatOptions) && ` · ${packWeightLabel(format, formatOptions)}`} · {format.barcode ? `Código ${format.barcode}` : 'Sin código'}</p>
                        {pendingDelete && <p className="mt-1 text-xs font-semibold text-red-700">Se retirará al guardar. El historial se conserva.</p>}
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 text-xs font-semibold sm:justify-end">
                      {!editing && canReadPrices && (
                        <button
                          type="button"
                          onClick={() => {
                            const params = new URLSearchParams();
                            if (branchId) params.set('branchId', String(branchId));
                            params.set('varietyPriceId', String(entry.id));
                            navigate(buildSettingsPath('prices', 'catalog-price-list', `?${params.toString()}`));
                          }}
                          className="text-blue-700 hover:underline"
                        >
                          Ir a lista de precios
                        </button>
                      )}
                      {editing && pendingDelete && (
                        <button type="button" onClick={() => undoDeleteFormat(formatKey)} disabled={saving} aria-label={`Deshacer eliminación del formato ${formatLabel}`} className="rounded-md border border-red-300 bg-white px-3 py-1.5 text-red-700 hover:bg-red-50 disabled:opacity-50">Deshacer</button>
                      )}
                      {editing && canManageFormats && !pendingDelete && (
                        <button type="button" onClick={() => stageDeleteFormat(formatKey)} disabled={saving} aria-label={`${isNew ? 'Quitar' : 'Retirar'} formato ${formatLabel}`} className="rounded-md border border-red-200 bg-white px-3 py-1.5 text-red-700 hover:bg-red-50 disabled:opacity-50">
                          {isNew ? 'Quitar' : 'Retirar'}
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="border-t border-gray-200 bg-gray-50/60">
                    <h5 id={`${detailsId}-heading`} className="px-2.5 py-2 text-sm font-semibold text-gray-700">Detalles del formato</h5>
                    <div id={detailsId} aria-labelledby={`${detailsId}-heading`} className="border-t border-gray-200 p-2.5">
                      {editing ? (
                        <fieldset disabled={saving || pendingDelete || !canManageFormats} className="grid grid-cols-1 gap-3 text-xs sm:grid-cols-2 xl:grid-cols-4">
                          {entry.sharesRetailConfiguration && <p className="text-slate-600 sm:col-span-2 xl:col-span-4">La unidad, el peso y la balanza se configuran en Detalle y se comparten con Oferta.</p>}
                          <label className="font-medium text-gray-700">
                            Unidad de venta
                            <input {...fieldProps(`${formatKey}.metricUnit`)} disabled={Boolean(entry.sharesRetailConfiguration)} value={entry.metricUnit} onChange={(event) => updateFormatField(formatKey, 'metricUnit', event.target.value)} className="mt-1 w-full rounded-md border border-gray-300 bg-white px-2.5 py-2 text-sm" />
                            {renderFieldError(`${formatKey}.metricUnit`)}
                          </label>
                          <label className="font-medium text-gray-700">
                            Formato
                            <select {...fieldProps(`${formatKey}.productFormatCode`)} value={entry.productFormatCode} onChange={(event) => updateFormatField(formatKey, 'productFormatCode', event.target.value)} className="mt-1 w-full rounded-md border border-gray-300 bg-white px-2.5 py-2 text-sm">
                              {currentCodeIsInactive && <option value={entry.productFormatCode} disabled>{getProductFormatLabel(entry.productFormatCode, [])} (inactivo)</option>}
                              {!entry.productFormatCode && <option value="">Selecciona un formato</option>}
                              {formatOptions.map((option) => <option key={option.value} value={option.value} disabled={!isRepeatableProductFormat(option) && option.value !== entry.productFormatCode && editSession.formats.some((other) => !other.pendingDelete && other.key !== entry.key && other.productFormatCode === option.value)}>{option.label}</option>)}
                            </select>
                            {renderFieldError(`${formatKey}.productFormatCode`)}
                          </label>
                          <label className="font-medium text-gray-700">
                            Código de barras
                            <input
                              {...fieldProps(`${formatKey}.barcode`)}
                              inputMode="numeric"
                              pattern="[0-9]{1,7}"
                              maxLength={7}
                              value={entry.barcode}
                              onChange={(event) => updateFormatField(formatKey, 'barcode', event.target.value.replace(/\D/g, '').slice(0, 7))}
                              className="mt-1 w-full rounded-md border border-gray-300 bg-white px-2.5 py-2 text-sm"
                            />
                            {renderFieldError(`${formatKey}.barcode`)}
                          </label>
                          <label className="font-medium text-gray-700">
                            Peso por unidad (kg)
                            <input {...fieldProps(`${formatKey}.unitWeightKg`)} type="number" min="0.001" step="0.001" disabled={Boolean(entry.sharesRetailConfiguration)} value={entry.unitWeightKg} onChange={(event) => updateFormatField(formatKey, 'unitWeightKg', event.target.value)} className="mt-1 w-full rounded-md border border-gray-300 bg-white px-2.5 py-2 text-sm" />
                            {renderFieldError(`${formatKey}.unitWeightKg`)}
                          </label>
                          {variety.inventoryBaseUnit === 'unidad' && <label className="font-medium text-gray-700">Unidades por pack
                            <input {...fieldProps(`${formatKey}.baseQuantityPerUnit`)} className="mt-1 w-full rounded-md border px-2.5 py-2" type="number" min="0.000001" step="0.000001" value={entry.baseQuantityPerUnit || ''} onChange={(event) => updateFormatField(formatKey, 'baseQuantityPerUnit', event.target.value)} />
                          </label>}
                          <label className="font-medium text-gray-700 sm:col-span-2 xl:col-span-3">
                            Descripción del precio
                            <input value={entry.description} onChange={(event) => updateFormatField(formatKey, 'description', event.target.value)} className="mt-1 w-full rounded-md border border-gray-300 bg-white px-2.5 py-2 text-sm" />
                          </label>
                          <label className="flex items-center gap-2 self-end rounded-md border border-gray-200 bg-white px-2.5 py-2 text-xs font-medium text-gray-700">
                            <input type="checkbox" disabled={Boolean(entry.sharesRetailConfiguration)} checked={Boolean(entry.useScaleWeight)} onChange={(event) => updateFormatField(formatKey, 'useScaleWeight', event.target.checked)} />
                            Usar peso de balanza
                          </label>
                          <div className="font-medium text-gray-700 sm:col-span-2 xl:col-span-4">
                            Imagen de presentación
                            <input aria-label={`Imagen de presentación ${formatLabel}`} type="file" accept="image/*" onChange={(event) => replaceFormatImage(formatKey, event.target.files?.[0] || null)} className="mt-1 block w-full text-xs" />
                            {(entry.imageFile || entry.priceImage) && (
                              <button type="button" onClick={() => removeFormatImage(formatKey)} className="mt-2 rounded-md border border-red-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-50">Quitar imagen de presentación</button>
                            )}
                          </div>
                        </fieldset>
                      ) : (
                        <>
                          <dl className="grid grid-cols-1 gap-2.5 text-xs sm:grid-cols-2 xl:grid-cols-4">
                            <div><dt className="font-semibold text-gray-600">Unidad de venta</dt><dd className="mt-1 text-gray-900">{entry.metricUnit || 'Sin unidad'}</dd></div>
                            <div><dt className="font-semibold text-gray-600">Formato</dt><dd className="mt-1 text-gray-900">{formatLabel}</dd></div>
                            <div><dt className="font-semibold text-gray-600">Código de barras</dt><dd className="mt-1 text-gray-900">{entry.barcode || 'Sin código'}</dd></div>
                            <div><dt className="font-semibold text-gray-600">Peso por unidad (kg)</dt><dd className="mt-1 text-gray-900">{Number(entry.unitWeightKg) > 0 ? `${entry.unitWeightKg} kg` : 'Sin peso'}</dd></div>
                          </dl>
                          <dl className="mt-2.5 grid grid-cols-1 gap-2.5 border-t border-gray-200 pt-2.5 text-xs md:grid-cols-[minmax(0,1fr)_minmax(220px,0.45fr)]">
                            <div><dt className="font-semibold text-gray-600">Descripción del precio</dt><dd className="mt-1 text-gray-900">{entry.description || 'Sin descripción'}</dd></div>
                            <div><dt className="font-semibold text-gray-600">Balanza</dt><dd className="mt-1 text-gray-900">{entry.useScaleWeight ? 'Usa peso' : 'No usa peso'}</dd></div>
                          </dl>
                        </>
                      )}
                    </div>
                  </div>
                </article>
              );
            })}
            {!editing && !formats.length && <p className="rounded-lg bg-gray-50 px-3 py-2 text-xs text-gray-500">Sin presentaciones.</p>}
            {editing && !editSession.formats.length && (
              <p className="rounded-lg border border-dashed border-gray-300 bg-gray-50 px-3 py-3 text-xs text-gray-500">Esta variedad no tiene formatos. Puedes agregar uno o guardar la variedad sin formatos.</p>
            )}
          </div>
          {editing && (
            <div>
              <button type="button" onClick={addFormat} disabled={saving || !canManageFormats || !unusedFormatOptions.length} className="rounded-lg border border-blue-200 bg-white px-3 py-2 text-xs font-semibold text-blue-700 hover:bg-blue-50 disabled:opacity-50">+ Agregar presentación</button>
              {!unusedFormatOptions.length && <p className="mt-1 text-xs text-gray-500">La variedad ya tiene todos los tipos de formato activos.</p>}
            </div>
          )}
        </div>
      </VarietyEditorDialog>
    </>
  );
};

export default PresentationItem;
